import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

/**
 * The primitives every screen is built from, so the design values live in one
 * place and a later screen cannot quietly introduce a second shadow or a
 * seventh radius.
 */

/* ── Buttons ────────────────────────────────────────────────────────────── */

type ButtonTone = 'primary' | 'secondary' | 'quiet' | 'danger' | 'destructive';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone;
  /** Shows a waiting state and blocks further clicks. */
  busy?: boolean;
  block?: boolean;
}

const BUTTON_TONES: Record<ButtonTone, string> = {
  // Green is the primary action, per the design system. Ink on green rather
  // than white on green: white fails contrast against this particular green.
  primary: 'bg-green text-ink font-bold border-transparent hover:bg-green/70',
  // Blue is structure — a secondary action that is still a real action.
  secondary: 'bg-brand text-page font-semibold border-transparent hover:bg-brand-deep',
  quiet: 'bg-card text-ink font-semibold border-divider hover:border-sky',
  danger: 'bg-card text-danger font-semibold border-divider hover:border-danger',
  // Filled, for the one button in a confirmation dialog that does the thing.
  // `danger` is the quiet version that sits in a row of other controls; this is
  // the version that has to read as the consequence.
  destructive: 'bg-danger text-page font-semibold border-transparent hover:bg-danger/85',
};

export function Button({
  tone = 'secondary',
  busy = false,
  block = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        'inline-flex items-center justify-center gap-[8px] rounded-pill border',
        'px-[20px] py-[12px] text-sub leading-none transition',
        'disabled:cursor-not-allowed disabled:opacity-55',
        BUTTON_TONES[tone],
        block && 'w-full',
        className,
      )}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

/** A link-looking control for the secondary action beside a real button. */
export function TextButton({
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={clsx(
        'border-0 bg-transparent p-0 text-sub font-semibold text-brand underline',
        'underline-offset-2 disabled:opacity-55',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="h-[14px] w-[14px] animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

/* ── Waiting ────────────────────────────────────────────────────────────── */

/**
 * The shapes a screen wears while its data is in flight.
 *
 * THE RULE, applied everywhere: a FIRST load shows the shape of what is coming
 * (a skeleton), and a RELOAD of something already on screen keeps the old rows
 * visible and marks them as stale. Replacing a table someone is reading with
 * the word "Loading" loses their place and makes a 300ms refresh look like a
 * page change.
 *
 * `aria-hidden` on the bars and a `role="status"` line beside them: the shapes
 * are decoration, the sentence is what gets announced.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden="true" className={clsx('animate-pulse rounded-xs bg-divider', className)} />;
}

/** A card of placeholder rows — what a table looks like before it arrives. */
export function SkeletonRows({ rows = 4, label = 'Loading…' }: { rows?: number; label?: string }) {
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex flex-col gap-[14px] p-[18px]">
        {Array.from({ length: rows }, (_, row) => (
          <div key={row} className="flex items-center gap-[18px]">
            <Skeleton className="h-[14px] flex-1" />
            <Skeleton className="h-[14px] w-[120px]" />
            <Skeleton className="h-[14px] w-[60px]" />
          </div>
        ))}
      </div>
      <p role="status" className="zs-sr-only">
        {label}
      </p>
    </Card>
  );
}

/**
 * A spinner and a few words, for a region that is refreshing in place or is
 * too small to deserve a skeleton.
 */
export function LoadingLine({ children = 'Loading…' }: { children?: ReactNode }) {
  return (
    <p role="status" className="m-0 flex items-center gap-[8px] text-sub text-muted">
      <Spinner />
      {children}
    </p>
  );
}

/**
 * Content that is on screen and being refreshed.
 *
 * Dimmed and inert rather than removed: the rows stay readable, and clicks are
 * blocked so nobody acts on a row that is about to be replaced.
 */
export function Refreshing({ busy, children }: { busy: boolean; children: ReactNode }) {
  return (
    <div
      aria-busy={busy || undefined}
      className={clsx(
        'transition-opacity duration-200',
        busy && 'pointer-events-none opacity-60',
      )}
    >
      {children}
    </div>
  );
}

/* ── Surfaces ───────────────────────────────────────────────────────────── */

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div className={clsx('rounded-feature bg-card shadow-card', padded && 'p-[28px]', className)}>
      {children}
    </div>
  );
}

/**
 * A figure and its caption. The number is the only condensed type on the
 * screen, which is what makes a row of these scannable at a glance.
 */
export function Stat({
  label,
  value,
  note,
  tone = 'brand',
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  tone?: 'brand' | 'muted' | 'good';
}) {
  const valueTone =
    tone === 'good' ? 'text-green-mid' : tone === 'muted' ? 'text-muted' : 'text-brand';

  return (
    <div className="flex flex-col gap-[4px] rounded-md bg-card p-[20px] px-[22px] shadow-tile">
      <p className="m-0 text-caption text-muted">{label}</p>
      <p className={clsx('zs-figure m-0', valueTone)}>{value}</p>
      {note && <p className="m-0 text-caption text-muted">{note}</p>}
    </div>
  );
}

export function Eyebrow({
  children,
  tone = 'light',
}: {
  children: ReactNode;
  tone?: 'light' | 'dark';
}) {
  return (
    <p className={clsx('zs-eyebrow m-0', tone === 'dark' ? 'text-green-soft' : 'text-green-mid')}>
      {children}
    </p>
  );
}

/** The heading block every screen opens with: eyebrow, title, a line of why. */
export function PageHeading({
  eyebrow,
  title,
  sub,
  actions,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-[20px]">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-[8px] mb-0 text-title font-semibold">{title}</h1>
        {sub && <p className="mt-[6px] mb-0 max-w-[68ch] text-body text-muted">{sub}</p>}
      </div>
      {actions}
    </div>
  );
}

/* ── Status ─────────────────────────────────────────────────────────────── */

export type StatusTone = 'healthy' | 'attention' | 'broken' | 'neutral';

const STATUS_STYLES: Record<StatusTone, { chip: string; glyph: string }> = {
  healthy: { chip: 'bg-green-soft/60 text-green-deep border-green-soft', glyph: '✓' },
  attention: { chip: 'bg-warning-soft text-warning border-[#eccf9b]', glyph: '!' },
  broken: { chip: 'bg-danger-soft text-danger border-[#e9b7b4]', glyph: '×' },
  neutral: { chip: 'bg-divider text-muted border-divider', glyph: '–' },
};

/**
 * A status chip. It always carries a glyph and a word, never colour alone —
 * both because "never convey status by colour alone" is a rule here and
 * because a facilities manager may well be printing this page in black and
 * white.
 */
export function StatusChip({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  const style = STATUS_STYLES[tone];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-[6px] rounded-chip border',
        'px-[10px] py-[4px] text-micro font-semibold',
        style.chip,
      )}
    >
      <span aria-hidden="true">{style.glyph}</span>
      {children}
    </span>
  );
}

/* ── Forms ──────────────────────────────────────────────────────────────── */

/**
 * The surface every toolbar control wears — the filter bars and the row selects
 * on the mapping screen.
 *
 * `Field` below is the LABELLED form input, for a screen where somebody is
 * entering something. A filter bar is a toolbar: same surface, smaller height,
 * label above as an eyebrow. Kept here so the two cannot drift apart, which is
 * exactly what happened when each screen wrote its own border and radius.
 */
export const CONTROL =
  'rounded-button border border-divider bg-card px-[12px] py-[9px] text-body text-ink ' +
  'transition-colors hover:border-sky focus:border-sky focus:outline-none';

/** Label + control, the way both filter bars lay one out. */
export function FilterField({
  label,
  children,
  grow = false,
}: {
  label: string;
  children: ReactNode;
  grow?: boolean;
}) {
  return (
    <label className={clsx('flex flex-col gap-[6px]', grow && 'min-w-[220px] flex-1')}>
      <span className="zs-eyebrow text-muted">{label}</span>
      {children}
    </label>
  );
}

/** A checkbox that reads as a control rather than as stray text on a card. */
export function TogglePill({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label
      className={clsx(
        'flex cursor-pointer items-center gap-[8px] rounded-pill border px-[14px] py-[10px]',
        'text-sub transition-colors',
        checked ? 'border-sky bg-sky-tint/25 text-ink' : 'border-divider hover:border-sky',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-brand"
      />
      {children}
    </label>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: ReactNode;
  error?: string | null;
}

export function Field({ label, hint, error, id, className, ...rest }: FieldProps) {
  const inputId = id ?? `field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const describedBy = [hint ? `${inputId}-hint` : null, error ? `${inputId}-error` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-[6px]">
      <label htmlFor={inputId} className="text-sub font-medium text-ink">
        {label}
      </label>
      <input
        {...rest}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={clsx(
          'rounded-button border bg-card px-[14px] py-[12px] text-body transition-colors',
          'text-ink placeholder:text-[#9a9aa1] focus:border-sky focus:outline-none',
          error ? 'border-danger' : 'border-divider',
          className,
        )}
      />
      {hint && (
        <p id={`${inputId}-hint`} className="m-0 text-caption text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${inputId}-error`}
          role="alert"
          className="m-0 flex items-center gap-[6px] text-caption text-danger"
        >
          <span aria-hidden="true">×</span>
          {error}
        </p>
      )}
    </div>
  );
}

/** The hairline "or" between two ways of doing the same thing. */
export function OrRule({ children = 'or' }: { children?: ReactNode }) {
  return (
    <div className="flex items-center gap-[12px] text-caption text-muted">
      <span className="h-px flex-1 bg-divider" />
      {children}
      <span className="h-px flex-1 bg-divider" />
    </div>
  );
}

/* ── Messages ───────────────────────────────────────────────────────────── */

/**
 * A message about something that went wrong, written for a facilities manager.
 * `role="alert"` so a screen reader announces it without the user hunting for
 * what changed.
 */
export function Notice({
  tone = 'attention',
  title,
  children,
}: {
  tone?: StatusTone;
  title: string;
  children?: ReactNode;
}) {
  const style = STATUS_STYLES[tone];
  return (
    <div
      role={tone === 'broken' ? 'alert' : 'status'}
      className={clsx('rounded-md border p-[14px] px-[18px] text-sub', style.chip)}
    >
      <p className="m-0 flex items-center gap-[8px] font-semibold">
        <span aria-hidden="true">{style.glyph}</span>
        {title}
      </p>
      {children && <div className="mt-[6px] opacity-90">{children}</div>}
    </div>
  );
}

/** Initials for an organization. Two letters, from up to two words. */
export function Initials({ name, size = 44 }: { name: string; size?: number }) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: size <= 32 ? 12 : 16 }}
      className={clsx(
        'flex shrink-0 items-center justify-center bg-brand font-bold text-page',
        size <= 32 ? 'rounded-button' : 'rounded-image',
      )}
    >
      {letters || '?'}
    </span>
  );
}
